export enum PriceRangeId {
  UNDER_15000 = 'under_15000',
  BETWEEN_15000_30000 = '15000_30000',
  BETWEEN_30000_70000 = '30000_70000',
  OVER_70000 = 'over_70000',

  // 카페 전용 구간
  CAFE_UNDER_5000 = 'cafe_under_5000',
  CAFE_5000_10000 = 'cafe_5000_10000',
  CAFE_10000_20000 = 'cafe_10000_20000',
  CAFE_OVER_20000 = 'cafe_over_20000',
}

export type PriceRangeOption = {
  id: PriceRangeId
  label: string
  min: number | null
  max: number | null
  icon: string
}

/** 레스토랑·술집에서 쓰는 기본 구간 */
export const PRICE_RANGE_OPTIONS: PriceRangeOption[] = [
  {
    id: PriceRangeId.UNDER_15000,
    label: '15,000원 이하',
    min: null,
    max: 15000,
    icon: '₩',
  },
  {
    id: PriceRangeId.BETWEEN_15000_30000,
    label: '15,000원 이상 30,000원 이하',
    min: 15000,
    max: 30000,
    icon: '₩₩',
  },
  {
    id: PriceRangeId.BETWEEN_30000_70000,
    label: '30,000원 이상 70,000원 이하',
    min: 30000,
    max: 70000,
    icon: '₩₩₩',
  },
  {
    id: PriceRangeId.OVER_70000,
    label: '70,000원 이상',
    min: 70000,
    max: null,
    icon: '₩₩₩₩',
  },
]

/**
 * 카페에서 쓰는 구간.
 *
 * 왜 따로 두나
 * -----------
 * 기본 구간(15,000 / 30,000 / 70,000)은 카페에서 변별력이 없었다.
 * 카페 기록 118건 중 89건(75%)이 '15,000원 이하' 한 칸에 몰려,
 * 커피 한 잔과 디저트 세트가 같은 ₩ 하나로 보였다.
 * (레스토랑은 51/40/8/2로 퍼지고 술집도 7/67/27로 괜찮아 그대로 둔다)
 *
 * 카페는 가격이 좁은 구간에 몰려 있어 눈금을 촘촘히 한다.
 */
export const CAFE_PRICE_RANGE_OPTIONS: PriceRangeOption[] = [
  {
    id: PriceRangeId.CAFE_UNDER_5000,
    label: '5,000원 이하',
    min: null,
    max: 5000,
    icon: '₩',
  },
  {
    id: PriceRangeId.CAFE_5000_10000,
    label: '5,000원 이상 10,000원 이하',
    min: 5000,
    max: 10000,
    icon: '₩₩',
  },
  {
    id: PriceRangeId.CAFE_10000_20000,
    label: '10,000원 이상 20,000원 이하',
    min: 10000,
    max: 20000,
    icon: '₩₩₩',
  },
  {
    id: PriceRangeId.CAFE_OVER_20000,
    label: '20,000원 이상',
    min: 20000,
    max: null,
    icon: '₩₩₩₩',
  },
]

/**
 * 카테고리가 카페인가.
 *
 * 같은 값이 두 형태로 흘러다닌다 — 피드는 코드('cafe'), 기록 작성 화면은
 * 이름('카페')을 넘긴다. 어느 쪽이 와도 알아보게 한다.
 */
export const isCafeCategory = (category?: string | null): boolean => {
  if (!category) return false
  const v = category.trim().toLowerCase()

  return v === 'cafe' || v === '카페'
}

/** 이 카테고리에서 고를 수 있는 가격 구간 */
export const getPriceRangeOptions = (
  category?: string | null
): PriceRangeOption[] =>
  isCafeCategory(category) ? CAFE_PRICE_RANGE_OPTIONS : PRICE_RANGE_OPTIONS

/**
 * 가격 범위에 해당하는 아이콘을 반환한다.
 *
 * 고른 구간과 똑같은 값이 오면 그 구간의 아이콘을 그대로 쓴다. 경계값이
 * 두 구간에 걸치기 때문에(30,000원은 15,000~30,000의 끝이면서 30,000~70,000의
 * 시작이다) 겹침으로 찾으면 앞 구간이 먼저 잡혀 ₩₩₩을 골라도 ₩₩가 나온다.
 *
 * 똑같은 값이 없는 기록은 최소 금액을 기준으로 구간을 정한다.
 * 카페 구간을 바꾸기 전에 찍힌 118건은 관리자가 전부 새 눈금으로 옮겨서
 * (2026-10-07) 따로 처리하던 표는 걷어냈다. 장소 분류가 나중에 바뀌는
 * 등으로 낯선 조합이 들어와도 아래 폴백이 받아낸다.
 *
 * @param minPrice 최소 1인당 가격대
 * @param maxPrice 최대 1인당 가격대
 * @param category 장소 카테고리 ('cafe' 또는 '카페'). 없으면 기본 구간으로 본다.
 * @returns 가격대 아이콘 (₩, ₩₩, ₩₩₩, ₩₩₩₩) 또는 빈 문자열
 */
export const getPriceRangeIcon = (
  minPrice: number | null,
  maxPrice: number | null,
  category?: string | null
): string => {
  if (minPrice === null && maxPrice === null) return ''

  const options = getPriceRangeOptions(category)

  const exact = options.find(
    (option) => option.min === minPrice && option.max === maxPrice
  )
  if (exact) return exact.icon

  if (minPrice === null) return options[0].icon

  // 뒤에서부터 훑어 최소 금액이 걸리는 첫 구간을 쓴다.
  // 구간이 카테고리마다 달라, 예전처럼 숫자를 직접 적으면 한쪽만 맞는다.
  for (let i = options.length - 1; i > 0; i--) {
    const floor = options[i].min
    if (floor !== null && minPrice >= floor) return options[i].icon
  }

  return options[0].icon
}
